import { getProductDataV2 } from './getProductDataV2';
import type { GetProductDataParamsV2, GetProductDataResponseV2 } from './types';

interface GetProductDataResponseV1 {
  status: string;
  name: string;
  code: string;
  vimeoVideoId: string | null;
  youtubeVideoId: string | null;
  amount: number;
  amountTax: number;
  standardAmount: number;
  standardTax: number;
  currency: string;
  amountMonthly: number;
  planType: string;
  planName: string;
  amountMonthlyTax: number;
  installments: PlanV1[];
  source: string;
  seller: string;
}

interface PlanV1 {
  id: number;
  value: string;
  tax: string;
}

export interface AdaptGetProductDataV2Params {
  productCode?: string;
  productId?: string;
  plan?: string;
  planType?: string;
  business?: string;
  operation?: string;
  voucher?: string;
  email?: string;
  referer?: string;
  leadId?: string;
  seller?: string;
  source?: string;
}

/**
 * Adapta parâmetros no formato V1 para o formato V2 e chama getProductDataV2.
 * Retorna a resposta no formato GetProductDataResponse para retrocompatibilidade.
 */
export async function getProductDataV2AsV1(
  params: AdaptGetProductDataV2Params,
  isServer?: boolean
): Promise<GetProductDataResponseV1> {
  const code = params.productCode || params.productId;

  if (!code) {
    throw new Error('productCode ou productId é obrigatório');
  }

  const v2Params: GetProductDataParamsV2 = {
    code,
    business: params.business,
    operation: params.operation,
    voucher: params.voucher,
    email: params.email,
    leadId: params.leadId,
    seller: params.seller,
    source: params.source,
    plans: params.plan ? [params.plan] : undefined,
    planTypes: params.planType ? [params.planType] : undefined,
  };

  const v2Response = await getProductDataV2(v2Params, isServer);

  return adaptV2ResponseToV1(v2Response, code);
}

/**
 * Converte GetProductDataResponseV2 para GetProductDataResponse.
 * Utiliza o primeiro plano quando há múltiplos.
 * Preserva campos extras (ex: paymentOptions, description) para retrocompatibilidade.
 */
export function adaptV2ResponseToV1(
  v2Response: GetProductDataResponseV2,
  code?: string
): GetProductDataResponseV1 {
  const firstPlan = v2Response.plans?.[0];

  if (!firstPlan) {
    throw new Error('Resposta do produto não contém planos');
  }

  const response: GetProductDataResponseV1 = {
    status: 'success',
    name: v2Response.name ?? firstPlan.planName ?? '',
    code: v2Response.code ?? code ?? firstPlan.code ?? '',
    vimeoVideoId: null,
    youtubeVideoId: null,
    amount: firstPlan.amount,
    amountTax: firstPlan.amountTax,
    standardAmount: firstPlan.standardAmount,
    standardTax: firstPlan.standardTax,
    currency: firstPlan.currency,
    amountMonthly: firstPlan.amountMonthly,
    planType: firstPlan.planType,
    planName: firstPlan.planName,
    amountMonthlyTax: firstPlan.amountMonthlyTax,
    installments:
      firstPlan.installments?.map((inst) => ({
        id: inst.id,
        value: inst.value,
        tax: inst.tax,
      })) ?? [],
    source: v2Response.source,
    seller: v2Response.seller,
  };

  // Preserva campos extras do backend (ex: paymentOptions, description para Livelo)
  const extras = {
    ...(firstPlan as unknown as Record<string, unknown>),
    ...(v2Response as unknown as Record<string, unknown>),
  };
  const knownKeys = new Set(Object.keys(response));
  Object.entries(extras).forEach(([key, value]) => {
    if (!knownKeys.has(key) && value !== undefined) {
      (response as unknown as Record<string, unknown>)[key] = value;
    }
  });

  return response;
}
